// Shared HTML builders + wiring for property "basics" form fields.
// Used by both NewPropertyView (wizard) and PropertyView (edit) so the
// two surfaces stay in sync — same field set, same v5 layout, same
// energy-rating preset/custom toggle, same amenity chip group.

import { esc } from '../core/component.js';
import { icon } from '../core/icons.js';
import { t, getLocale } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { ENERGY_RATINGS, AMENITY_CATALOG, AMENITY_CATALOG_SECONDARY, decodeAmenities } from '../core/propertyCatalogs.js';
import { api, ApiError } from '../core/api.js';
import { countryOptions } from '../core/countries.js';
import { lookupHuPostalCity } from '../core/huPostalCodes.js';
import { parseHungarianAddress } from '../core/address.js';

/**
 * Address field rows in the canonical Hungarian order:
 * irányítószám → település → közterület (utca + házszám) → helyrajzi szám.
 * Country lives in a compact trailing row and defaults to HU since
 * everything else assumes Hungary; we keep it editable for the rare
 * cross-border case but de-emphasize it.
 */
export function addressFieldsHtml(values = {}) {
  const v = {
    address_line1: values.address_line1 || '',
    postal_code: values.postal_code || '',
    city: values.city || '',
    country: values.country || 'HU',
    helyrajzi_szam: values.helyrajzi_szam || '',
  };
  return `
    ${addressPasteFieldHtml()}
    <div class="field-row" style="grid-template-columns: 160px 1fr;">
      <div class="field"><label>${esc(t('properties.postal_code'))}</label>
        <input class="input" name="postal_code" maxlength="16" placeholder="${esc(postalPlaceholder(v.country))}" value="${esc(v.postal_code)}" /></div>
      <div class="field"><label>${esc(t('properties.city'))}</label>
        <input class="input" name="city" required placeholder="Budapest" value="${esc(v.city)}" /></div>
    </div>
    <div class="field">
      <label>${esc(t('property.wizard.basics.street_label'))}</label>
      <input class="input" name="address_line1" required placeholder="${esc(t('property.wizard.basics.street_placeholder'))}" value="${esc(v.address_line1)}" />
    </div>
    <div class="field-row" style="grid-template-columns: 220px 1fr;">
      <div class="field"><label class="faint">${esc(t('properties.country'))}</label>
        ${countryFieldHtml(v.country)}
      </div>
      <div></div>
    </div>
  `;
}

/**
 * Single "paste the full address" helper input. `wireAddressPaste` (called
 * by `wireAddressFields`, or directly) parses it (parseHungarianAddress)
 * and fills the structured fields around it — line1-style forms get
 * address_line1, unit-style forms get the addr_* breakdown. `prefix`
 * namespaces the input name for forms whose fields are prefixed (e.g. the
 * property wizard's first-unit `unit_` fields).
 */
export function addressPasteFieldHtml(prefix = '') {
  return `
    <div class="field">
      <label class="faint">${esc(t('properties.address_paste_label') || 'Gyors kitöltés — teljes cím beillesztése')}</label>
      <input class="input" name="${esc(prefix)}address_paste" autocomplete="off"
        placeholder="1061 Budapest, Andrássy út 12. B ép. 2. lph. 3. em. 5. ajtó" />
    </div>`;
}

/**
 * Wire the "paste the full address" helper (addressPasteFieldHtml) to the
 * structured fields around it. Field names are alias-aware so the one
 * wiring serves every address form in the app:
 *   postal   → postal_code | billing_postal_code
 *   city     → city | billing_city
 *   detailed → addr_street (+ addr_street_type select when present),
 *              addr_house_number, addr_building, addr_staircase,
 *              floor | addr_floor, addr_door
 *   fallback → street + house number joined into
 *              address_line1 | billing_address_line1
 * Only non-empty parsed parts are written, and every write dispatches
 * input/change events so state mirrors and the postal→city auto-fill stay
 * in sync — the user reviews/corrects the fields afterwards. `onApply`
 * fires after a successful parse.
 */
export function wireAddressPaste(formEl, { prefix = '', onApply } = {}) {
  if (!formEl) return;
  const pasteInput = formEl.querySelector(`[name="${prefix}address_paste"]`);
  if (!pasteInput) return;
  const find = (...names) => {
    for (const n of names) {
      const el = formEl.querySelector(`[name="${prefix}${n}"]`);
      if (el) return el;
    }
    return null;
  };
  const fill = (el, value) => {
    if (!el || !value) return;
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const apply = () => {
    const parsed = parseHungarianAddress(pasteInput.value);
    if (!parsed) return;
    fill(find('postal_code', 'billing_postal_code'), parsed.postal_code);
    fill(find('city', 'billing_city'), parsed.city);
    const streetEl = find('addr_street');
    if (streetEl) {
      // Forms with a separate street-type <select> (pl. beköltöztető
      // varázsló) store "Andrássy" + "út" apart — peel the type off the
      // parsed street when it matches one of the select's options.
      let street = parsed.street;
      const typeSel = find('addr_street_type');
      if (typeSel && street) {
        const parts = street.split(' ');
        const last = parts[parts.length - 1].toLowerCase();
        const opt = Array.from(typeSel.options)
          .find((o) => (o.value || o.textContent).trim().toLowerCase() === last);
        if (opt && parts.length > 1) {
          street = parts.slice(0, -1).join(' ');
          fill(typeSel, (opt.value || opt.textContent).trim());
        }
      }
      fill(streetEl, street);
      fill(find('addr_house_number'), parsed.house_number);
      fill(find('addr_building'), parsed.building);
      fill(find('addr_staircase'), parsed.staircase);
      fill(find('floor', 'addr_floor'), parsed.floor);
      fill(find('addr_door'), parsed.door);
    } else {
      const line1 = [parsed.street, parsed.house_number].filter(Boolean).join(' ');
      fill(find('address_line1', 'billing_address_line1'), line1);
    }
    if (typeof onApply === 'function') onApply();
  };
  pasteInput.addEventListener('change', apply);
  pasteInput.addEventListener('paste', () => setTimeout(apply, 0));
}

/**
 * Renders a country `<select>` whose value is the ISO 3166-1 alpha-2 code
 * the backend reads directly (no hidden field / datalist anymore — a plain
 * dropdown is more consistent and far friendlier on mobile).
 *
 * `currentCode` is the initial ISO code ('' = blank). `opts.name` sets the
 * field name (default 'country'; billing forms pass 'billing_country').
 * `opts.allowBlank` prepends an empty option (used by unit address
 * overrides where blank means "inherit from the parent property"); when it
 * is false (property semantics) and no code is given the browser falls back
 * to the first option, which is HU.
 */
export function countryFieldHtml(currentCode = '', opts = {}) {
  const locale = getLocale();
  const code = (currentCode || '').toUpperCase();
  const name = opts.name || 'country';
  const allowBlank = opts.allowBlank === true;
  const blankOpt = allowBlank
    ? `<option value="" ${code === '' ? 'selected' : ''}>${esc(opts.blankLabel || '—')}</option>`
    : '';
  const options = countryOptions(locale)
    .map((o) => `<option value="${esc(o.code)}" ${o.code === code ? 'selected' : ''}>${esc(o.label)}</option>`)
    .join('');
  return `<select class="select" name="${esc(name)}">${blankOpt}${options}</select>`;
}

// Placeholder hint per country. Most ISO countries we hand-back a
// generic example; the canonical HU 4-digit pattern stays prominent
// because most users only ever fill in HU addresses here.
const POSTAL_PLACEHOLDERS = {
  HU: '1051', AT: '1010', DE: '10115', RO: '010101', SK: '811 01',
  SI: '1000', HR: '10000', CZ: '110 00', PL: '00-001', UA: '01001',
  RS: '11000', IT: '00100', FR: '75001', ES: '28001', NL: '1011 AA',
  BE: '1000', GB: 'SW1A 1AA', IE: 'D02 XY00', US: '94103', CA: 'K1A 0B1',
};
function postalPlaceholder(country) {
  const c = (country || 'HU').toUpperCase();
  return POSTAL_PLACEHOLDERS[c] || '';
}



/**
 * "Main unit" basics for the property creation form. Most landlords
 * here are renting a single apartment, so the property and its unit
 * are conceptually the same thing — size, rent, közös költség,
 * furnished, max occupants belong on Step 1. On submit these flow
 * into the auto-created default unit. Multi-unit landlords leave it
 * empty and add real units on Step 2.
 *
 * @param {object} values  { size_sqm, asking_rent, kozos_koltseg, furnished, max_occupants }
 * @param {object} opts    { currency?: string }
 */
export function unitBasicsSectionHtml(values = {}, opts = {}) {
  const cur = esc(opts.currency || 'HUF');
  const v = {
    size_sqm: values.size_sqm ?? '',
    asking_rent: values.asking_rent ?? '',
    kozos_koltseg: values.kozos_koltseg ?? '',
    furnished: values.furnished || 'unfurnished',
    max_occupants: values.max_occupants ?? '',
  };
  const fOpt = (k, label) => `<option value="${k}" ${v.furnished === k ? 'selected' : ''}>${esc(label)}</option>`;
  return `
    <div class="wizard-section">
      <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.basics.unit_section') || 'Main unit · Egység adatai')}</div>
      <div class="field-row">
        <div class="field"><label>${esc(t('property.unit_form.size') || 'Size (m²)')}</label>
          <input class="input" name="size_sqm" type="number" step="0.01" min="0" value="${esc(v.size_sqm)}" /></div>
        <div class="field"><label>${esc(t('property.unit_form.asking_rent') || 'Asking rent / month')} <span class="faint" style="text-transform: none; letter-spacing: 0; font-size: 12px;">(${cur})</span></label>
          <input class="input" name="asking_rent" type="number" step="1" min="0" value="${esc(v.asking_rent)}" /></div>
      </div>
      <div class="field-row">
        <div class="field"><label>${esc(t('property.unit_form.kozos_koltseg') || 'Közös költség / mo')} <span class="faint" style="text-transform: none; letter-spacing: 0; font-size: 12px;">(${cur})</span></label>
          <input class="input" name="kozos_koltseg" type="number" step="1" min="0" value="${esc(v.kozos_koltseg)}" placeholder="0" /></div>
        <div class="field"><label>${esc(t('property.unit_form.max_occupants') || 'Max occupants')}</label>
          <input class="input" name="max_occupants" type="number" step="1" min="0" value="${esc(v.max_occupants)}" placeholder="—" /></div>
      </div>
      <div class="field"><label>${esc(t('property.unit_form.furnished') || 'Furnished')}</label>
        <select class="select" name="furnished">
          ${fOpt('unfurnished',      t('property.unit_form.furnished_unfurnished') || 'Unfurnished')}
          ${fOpt('partly_furnished', t('property.unit_form.furnished_partly') || 'Partly furnished')}
          ${fOpt('fully_furnished',  t('property.unit_form.furnished_fully') || 'Fully furnished')}
        </select>
      </div>
      <span class="faint" style="margin-top: 6px; display: block;">${esc(t('property.wizard.basics.unit_section_help') || 'These describe the main rentable unit. Add more units (e.g. a garage) on the next step.')}</span>
    </div>
  `;
}

/**
 * Furnished selector as a segmented pill control (single choice). Renders a
 * hidden `furnished` input so submit handlers / FormData read it like any
 * field. Wire it with wireFurnishedRadio() to make the segments interactive.
 *
 * @param {string} value  one of unfurnished | partly_furnished | fully_furnished
 */
export function furnishedRadioHtml(value = 'unfurnished') {
  const v = ['unfurnished', 'partly_furnished', 'fully_furnished'].includes(value) ? value : 'unfurnished';
  const opts = [
    ['unfurnished',      t('property.unit_form.furnished_unfurnished') || 'Unfurnished'],
    ['partly_furnished', t('property.unit_form.furnished_partly')      || 'Partly furnished'],
    ['fully_furnished',  t('property.unit_form.furnished_fully')       || 'Fully furnished'],
  ];
  const buttons = opts.map(([k, label]) => {
    const on = v === k;
    return `<button type="button" class="segmented-opt${on ? ' is-on' : ''}" data-furnished-opt="${k}" aria-pressed="${on ? 'true' : 'false'}">${esc(label)}</button>`;
  }).join('');
  return `<div class="segmented" data-furnished role="radiogroup" aria-label="${esc(t('property.unit_form.furnished') || 'Furnished')}">
    ${buttons}
    <input type="hidden" name="furnished" value="${esc(v)}" />
  </div>`;
}

/**
 * Wire a furnishedRadioHtml control inside `formEl`: clicking a segment sets
 * the hidden input, moves the active state, and fires onChange(value).
 */
export function wireFurnishedRadio(formEl, { onChange } = {}) {
  if (!formEl) return;
  const group = formEl.querySelector('[data-furnished]');
  if (!group) return;
  const hidden = group.querySelector('input[name="furnished"]');
  group.querySelectorAll('[data-furnished-opt]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const val = btn.getAttribute('data-furnished-opt');
      if (hidden) hidden.value = val;
      group.querySelectorAll('[data-furnished-opt]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (typeof onChange === 'function') onChange(val);
    });
  });
}

/**
 * Energy certificate section — rating preset + custom override, certificate
 * number, expiry date. Uses the v5 `.wizard-section` divider so it sits
 * inside any stack form (both wizard and edit surfaces).
 *
 * @param {object} values  { energy_rating, energy_certificate_number, energy_certificate_expires_at }
 * @param {object} opts    { showDropzone?: bool, showHelp?: bool }
 */
export function energySectionHtml(values = {}, opts = {}) {
  const rating = (values.energy_rating || '').toString().toUpperCase();
  const certNo = values.energy_certificate_number || '';
  const certExp = values.energy_certificate_expires_at || '';
  const isPreset = rating === '' || ENERGY_RATINGS.includes(rating);
  const showDropzone = !!opts.showDropzone;
  const showHelp = opts.showHelp !== false;
  const ratingOpts = [
    `<option value="" ${rating === '' ? 'selected' : ''}>${esc(t('common.none'))}</option>`,
    ...ENERGY_RATINGS.map((r) => `<option value="${r}" ${rating === r ? 'selected' : ''}>${r}</option>`),
    `<option value="__other__" ${!isPreset ? 'selected' : ''}>${esc(t('common.other'))}</option>`,
  ].join('');
  // Energy class and expiry date are sourced from OÉNY based on the
  // HET code — the user types the cert number, blur fires a lookup,
  // and these two fields fill themselves. We mark them readonly /
  // disabled so the values can't drift from what the registry says;
  // the only path to changing them is editing the HET code.
  return `
    <div class="wizard-section">
      <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.basics.energy_section') || 'Energy certificate · E-tanúsítvány')}</div>
      <div class="field-row three">
        <div class="field"><label>${esc(t('property.wizard.basics.energy_rating') || 'Energy rating')}</label>
          <select class="select" name="energy_rating_preset" disabled aria-readonly="true">${ratingOpts}</select></div>
        <div class="field"><label>${esc(t('property.wizard.basics.cert_number') || 'Certificate №')}</label>
          <input class="input" name="energy_certificate_number" value="${esc(certNo)}" placeholder="HET-0010-0000" maxlength="64" /></div>
        <div class="field"><label>${esc(t('property.wizard.basics.cert_expires') || 'Expires')}</label>
          <input class="input" type="date" name="energy_certificate_expires_at" value="${esc(certExp)}" readonly /></div>
      </div>
      <div class="field" data-energy-other ${isPreset ? 'hidden' : ''} style="margin-top: 10px;">
        <label>${esc(t('property.wizard.basics.energy_other') || 'Custom rating')}</label>
        <input class="input" name="energy_rating_custom" value="${esc(isPreset ? '' : rating)}" placeholder="A++ / B" maxlength="16" readonly /></div>
      <input type="hidden" name="energy_rating" value="${esc(rating)}" />
      ${showDropzone ? `
        <div class="drop" style="margin-top: 14px; padding: 22px 18px;">
          <div class="drop-ic">${icon('file-text', { size: 'sm' })}</div>
          <div class="drop-t">${esc(t('property.wizard.basics.cert_drop_title') || 'Drop the E-tanúsítvány PDF')}</div>
          <div class="drop-m">${esc(t('property.wizard.basics.cert_drop_sub') || 'Upload after the property is created')}</div>
        </div>` : ''}
      ${showHelp ? `<span class="faint" style="margin-top: 10px; display: block;">${esc(t('property.wizard.basics.energy_help') || '')}</span>` : ''}
    </div>
  `;
}

/**
 * Amenities chip grid wrapped in a `.wizard-section`. `selected` may be an
 * array of keys or a raw amenities column (string/array) — both are
 * decoded. Chips are icon-less for a uniform v5 look. Secondary (less
 * common) amenities sit behind a "Further amenities" disclosure that
 * auto-opens when one of them is already selected. Both groups carry
 * `data-amenity`, so wiring + payload-collection treat them identically.
 */
export function amenitiesSectionHtml(selected) {
  const set = new Set(Array.isArray(selected) ? selected : decodeAmenities(selected));
  // Amenities exist only to power public-listing search filters, so when the
  // account has no public presence (marketplace disabled) we omit the whole
  // section. We still render it when something is already selected, so an
  // edit surface never silently hides stored data.
  if (!auth.marketplaceEnabled && set.size === 0) return '';
  const chip = (a) => {
    const on = set.has(a.key);
    return `<button type="button" class="chip${on ? ' chip-on' : ''}" data-amenity="${esc(a.key)}" aria-pressed="${on ? 'true' : 'false'}"><span>${esc(t('property.amenity.' + a.key) || a.key)}</span></button>`;
  };
  const primaryHtml = AMENITY_CATALOG.map(chip).join('');
  const secondaryOpen = AMENITY_CATALOG_SECONDARY.some((a) => set.has(a.key));
  const secondaryHtml = AMENITY_CATALOG_SECONDARY.map(chip).join('');
  return `
    <div class="wizard-section">
      <div class="eyebrow" style="margin-bottom: 12px;">${esc(t('property.wizard.basics.amenities') || 'Building amenities')} <span class="faint" style="text-transform: none; letter-spacing: 0;">— ${esc(t('property.wizard.basics.amenities_subtitle') || 'shown on public listings')}</span></div>
      <div class="chip-grid" data-amenities>${primaryHtml}</div>
      ${AMENITY_CATALOG_SECONDARY.length ? `
        <details class="amenities-more" ${secondaryOpen ? 'open' : ''} style="margin-top: 12px;">
          <summary class="faint" style="cursor: pointer; font-size: 12.5px;">${esc(t('property.wizard.basics.amenities_more') || 'Further amenities')}</summary>
          <div class="chip-grid" data-amenities-secondary style="margin-top: 10px;">${secondaryHtml}</div>
        </details>` : ''}
    </div>
  `;
}

/**
 * Wire the energy-rating preset/custom inputs in `formEl`. The hidden
 * `energy_rating` field is kept in sync so submit handlers can read it
 * directly off the form. `onChange(value)` fires after each user action
 * with the canonical (uppercased) rating value (may be '').
 */
export function wireEnergyRatingInputs(formEl, { onChange } = {}) {
  if (!formEl) return;
  const presetSel = formEl.querySelector('[name="energy_rating_preset"]');
  const hidden = formEl.querySelector('[name="energy_rating"]');
  const otherWrap = formEl.querySelector('[data-energy-other]');
  const customInp = formEl.querySelector('[name="energy_rating_custom"]');
  const emit = (v) => { if (typeof onChange === 'function') onChange(v); };
  if (presetSel) {
    presetSel.addEventListener('change', () => {
      const v = presetSel.value;
      if (v === '__other__') {
        if (otherWrap) otherWrap.hidden = false;
        const cv = (customInp?.value || '').trim().toUpperCase();
        if (hidden) hidden.value = cv;
        customInp?.focus();
        emit(cv);
      } else {
        if (otherWrap) otherWrap.hidden = true;
        if (hidden) hidden.value = v;
        emit(v);
      }
    });
  }
  if (customInp) {
    customInp.addEventListener('input', () => {
      const cv = (customInp.value || '').trim().toUpperCase();
      if (hidden) hidden.value = cv;
      emit(cv);
    });
  }
}

/**
 * Wire the amenity chip group inside `formEl`. Maintains the chip-on
 * class + aria-pressed on click and reports the resulting key list via
 * `onChange(amenities)`. Returns a getter for the current list.
 */
export function wireAmenitiesChips(formEl, { initial, onChange } = {}) {
  if (!formEl) return () => [];
  const list = Array.isArray(initial) ? [...initial] : [];
  const emit = () => { if (typeof onChange === 'function') onChange([...list]); };
  formEl.querySelectorAll('[data-amenity]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const key = btn.getAttribute('data-amenity');
      if (!key) return;
      const idx = list.indexOf(key);
      if (idx >= 0) {
        list.splice(idx, 1);
        btn.classList.remove('chip-on');
        btn.setAttribute('aria-pressed', 'false');
      } else {
        list.push(key);
        btn.classList.add('chip-on');
        btn.setAttribute('aria-pressed', 'true');
      }
      emit();
    });
  });
  return () => [...list];
}

/**
 * Collect the canonical property payload from a form built with the
 * helpers above. Strips the helper-only `energy_rating_preset` /
 * `energy_rating_custom` fields, normalizes the rating to uppercase
 * (null when empty), and replaces the `amenities` slot with the chip
 * group selection.
 */
export function collectBasicsPayload(formEl) {
  const payload = Object.fromEntries(new FormData(formEl).entries());
  delete payload.energy_rating_preset;
  delete payload.energy_rating_custom;
  // country_label is the user-visible localized country name — the
  // canonical ISO code lives in the hidden `country` field.
  delete payload.country_label;
  if (payload.energy_rating != null) {
    const v = String(payload.energy_rating).trim().toUpperCase();
    payload.energy_rating = v === '' ? null : v;
  }
  if (payload.energy_certificate_number != null) {
    const v = String(payload.energy_certificate_number).trim();
    payload.energy_certificate_number = v === '' ? null : v;
  }
  if (payload.energy_certificate_expires_at != null) {
    const v = String(payload.energy_certificate_expires_at).trim();
    payload.energy_certificate_expires_at = v === '' ? null : v;
  }
  if (payload.helyrajzi_szam != null) {
    const v = String(payload.helyrajzi_szam).trim();
    payload.helyrajzi_szam = v === '' ? null : v;
  }
  // Only emit amenities when the form actually renders the chip group.
  // After migration 0084 amenities live on the unit; the property edit form
  // no longer carries chips and must NOT send an empty list (that would
  // wipe the cached headline that syncAddressFromMainUnit otherwise refills
  // from the primary unit).
  if (formEl.querySelector('[data-amenity]')) {
    payload.amenities = Array.from(formEl.querySelectorAll('[data-amenity].chip-on'))
      .map((b) => b.getAttribute('data-amenity'))
      .filter(Boolean);
  }
  return payload;
}

/**
 * Wire the address-row behaviors:
 *   1. Country `<input>` reads from a `<datalist>` of localized country
 *      names. When the user picks (or types) a known label, the hidden
 *      `country` field is set to the ISO code so submit handlers keep
 *      receiving 'HU', 'DE', etc.
 *   2. Postal-code → city auto-fill. When country is HU and the postal
 *      input contains a 4-digit code we know, we fill the city — but
 *      only if the city field is empty OR was previously auto-filled
 *      (so a manual edit isn't clobbered on the next keystroke).
 *
 * `onChange(values)` fires after either field changes with the current
 * snapshot so wizard state can mirror it.
 */
export function wireAddressFields(formEl, { onChange, prefix = '', requireDefault = true } = {}) {
  if (!formEl) return;
  // `prefix` lets the same wiring drive a set of address inputs whose names
  // are namespaced (e.g. the wizard's first-unit address uses `unit_`
  // prefixed names so they route into state.unit rather than the property).
  // `requireDefault` is the property semantics: empty country snaps back to
  // 'HU' so the form never submits a blank. Unit address overrides pass
  // `requireDefault: false` so blank means "inherit from the parent
  // property" and stays blank.
  const postal = formEl.querySelector(`[name="${prefix}postal_code"]`);
  const city = formEl.querySelector(`[name="${prefix}city"]`);
  const country = formEl.querySelector(`[name="${prefix}country"]`); // <select> holding the ISO code
  let autoFilledCity = '';

  const emit = () => {
    if (typeof onChange !== 'function') return;
    onChange({
      postal_code: postal?.value || '',
      city: city?.value || '',
      country: country?.value || '',
    });
  };

  // Track the last real country code so the postal-format heuristic has
  // something to fall back on. Defaults to whatever was rendered.
  let lastValidCode = (country?.value || '').toUpperCase();

  const applyPostalConstraints = (cc) => {
    if (!postal) return;
    if (cc === 'HU') {
      postal.setAttribute('inputmode', 'numeric');
      postal.setAttribute('maxlength', '4');
    } else {
      postal.removeAttribute('inputmode');
      postal.setAttribute('maxlength', '16');
    }
    postal.setAttribute('placeholder', postalPlaceholder(cc));
  };
  applyPostalConstraints(lastValidCode || 'HU');

  if (country) {
    // The country is now a plain `<select>` holding the ISO code, so the
    // only thing to wire is: keep the postal-code format heuristic and the
    // wizard-state snapshot in sync when the selection changes.
    country.addEventListener('change', () => {
      const code = (country.value || '').toUpperCase();
      lastValidCode = code || (requireDefault ? 'HU' : '');
      applyPostalConstraints(code || 'HU');
      emit();
    });
  }

  if (postal && city) {
    const tryAutoFillCity = () => {
      const cc = (country?.value || 'HU').toUpperCase();
      if (cc !== 'HU') return;
      const hit = lookupHuPostalCity(postal.value);
      if (!hit) return;
      // Don't overwrite a user-entered city; only fill empty or
      // previous auto-fill values. District is appended for Budapest
      // so two adjacent postal codes don't both surface as just
      // "Budapest".
      const candidate = hit.district ? `Budapest ${hit.district}.` : hit.city;
      const current = city.value.trim();
      if (current === '' || current === autoFilledCity) {
        city.value = candidate;
        autoFilledCity = candidate;
        // Reflect into wizard state.
        city.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    postal.addEventListener('input', tryAutoFillCity);
    postal.addEventListener('change', tryAutoFillCity);
    // Run once on mount so re-entered drafts get filled too.
    tryAutoFillCity();
  }

  // Street autocomplete: the property form names its street input
  // `address_line1`, unit forms use `addr_street` — wire whichever is
  // present so every wireAddressFields caller gets it for free.
  const street = formEl.querySelector(`[name="${prefix}address_line1"], [name="${prefix}addr_street"]`);
  if (street) wireStreetAutocomplete(street, { postal, city, country });

  // "Paste the full address" helper (addressPasteFieldHtml): parse the
  // pasted line and fill whichever structured fields the form has.
  wireAddressPaste(formEl, { prefix, onApply: emit });
}

// ---------------------------------------------------------------------------
// Street-name autocomplete against GET /geo/streets (OSM gazetteer).
// A shared <datalist> is attached to the input and refilled with debounced
// prefix-search results; postal code / city narrow the search when filled.

let streetDatalistSeq = 0;
const streetCache = new Map(); // query-key -> rows (session-scoped)

function wireStreetAutocomplete(input, { postal, city, country } = {}) {
  const listId = `geo-street-dl-${++streetDatalistSeq}`;
  const datalist = document.createElement('datalist');
  datalist.id = listId;
  input.insertAdjacentElement('afterend', datalist);
  input.setAttribute('list', listId);
  input.setAttribute('autocomplete', 'off');

  let timer = null;
  let lastKey = '';

  const refill = (rows) => {
    datalist.replaceChildren(...rows.map((r) => {
      const opt = document.createElement('option');
      opt.value = r.street;
      return opt;
    }));
  };

  const lookup = async () => {
    const cc = (country?.value || 'HU').toUpperCase();
    // The gazetteer is Hungary-only; suggestions abroad would mislead.
    if (cc && cc !== 'HU') { refill([]); return; }
    // Only the street *name* prefix is useful — once the user starts the
    // house number ("Fő utca 12"), stop querying and clear the list.
    const q = input.value.trim();
    if (q.length < 2 || /\d/.test(q)) { refill([]); return; }
    const pc = (postal?.value || '').trim();
    const ct = (city?.value || '').trim();
    const params = new URLSearchParams({ q });
    if (/^\d{4}$/.test(pc)) params.set('postal_code', pc);
    if (ct) params.set('city', ct);
    const key = params.toString();
    if (key === lastKey) return;
    lastKey = key;
    if (streetCache.has(key)) { refill(streetCache.get(key)); return; }
    try {
      const res = await api.get(`/geo/streets?${key}`);
      const rows = Array.isArray(res?.data) ? res.data : [];
      streetCache.set(key, rows);
      // Ignore stale responses that resolve after a newer query.
      if (key === lastKey) refill(rows);
    } catch {
      // Autocomplete is best-effort: network/auth errors just mean no
      // suggestions, never a broken form.
    }
  };

  input.addEventListener('input', () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(lookup, 200);
  });
}

/**
 * Wire the silent OÉNY HET-code lookup. When the user finishes typing
 * (blur / change) a HET code, we POST it to /energy-certificates/lookup
 * and fill the read-only energy class + expiry date from the registry
 * response. The "Verify on OÉNY" button is gone — OÉNY is the only
 * source of truth for these fields, so a manual inline verdict adds no
 * value over silently filling the form.
 */
export function wireEnergyCertVerify(formEl, { onMatch } = {}) {
  if (!formEl) return;
  const certInput = formEl.querySelector('[name="energy_certificate_number"]');
  if (!certInput) return;

  const setEnergyRating = (raw) => {
    const v = String(raw || '').trim().toUpperCase();
    if (v === '') return;
    const presetSel = formEl.querySelector('[name="energy_rating_preset"]');
    const hidden = formEl.querySelector('[name="energy_rating"]');
    const customInp = formEl.querySelector('[name="energy_rating_custom"]');
    const otherWrap = formEl.querySelector('[data-energy-other]');
    if (hidden) hidden.value = v;
    if (presetSel) {
      const matchingOption = Array.from(presetSel.options).find((o) => o.value === v);
      if (matchingOption) {
        presetSel.value = v;
        if (otherWrap) otherWrap.hidden = true;
        if (customInp) customInp.value = '';
      } else {
        // Value isn't in our preset list (e.g. legacy single-letter
        // ratings from older certificates) — fall through to "Other"
        // so the user still sees what was filled and can adjust.
        presetSel.value = '__other__';
        if (otherWrap) otherWrap.hidden = false;
        if (customInp) customInp.value = v;
      }
      // Let wireEnergyRatingInputs's own change handler run so any
      // host onChange callback fires (state mirror, etc.).
      presetSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
  const fillFieldsFrom = (row) => {
    // Expiry date.
    if (row?.expirationDate) {
      const exp = formEl.querySelector('[name="energy_certificate_expires_at"]');
      if (exp && !exp.value) {
        exp.value = String(row.expirationDate).slice(0, 10);
        exp.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    // Energy class — always tries, but setEnergyRating respects the
    // existing preset/custom split.
    if (row?.energyClassification) {
      const hidden = formEl.querySelector('[name="energy_rating"]');
      if (hidden && (hidden.value || '').trim() === '') {
        setEnergyRating(row.energyClassification);
      }
    }
    // Cert number — fill from HRSZ-lookup results when the user hasn't
    // typed one yet, so the next HET-blur run is a no-op (deduped).
    if (row?.hetString) {
      const cert = formEl.querySelector('[name="energy_certificate_number"]');
      if (cert && (cert.value || '').trim() === '') {
        cert.value = String(row.hetString);
        cert.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  const sortMatches = (matches) => {
    return [...matches].sort((a, b) => {
      const da = String(a.creationDate || a.expirationDate || '');
      const db = String(b.creationDate || b.expirationDate || '');
      return db.localeCompare(da);
    });
  };

  const runLookup = async (certNo) => {
    try {
      const res = await api.post('/energy-certificates/lookup', { het: certNo });
      const matches = Array.isArray(res?.matches) ? res.matches : [];
      if (matches.length === 0) return null;
      const sorted = sortMatches(matches);
      const row = sorted.find((m) => m.is_valid) || sorted[0];
      fillFieldsFrom(row);
      if (typeof onMatch === 'function') {
        try { onMatch(row); } catch (_) { /* host errors shouldn't break the UI */ }
      }
      return row;
    } catch (_) {
      // Silent — class + expiry stay empty if the registry hiccups.
      return null;
    }
  };

  let lastVerified = '';
  const maybeAutoVerify = () => {
    const v = (certInput.value || '').trim();
    if (v === '' || v === lastVerified) return;
    const digits = v.replace(/\D+/g, '');
    if (digits.length < 4) return;
    lastVerified = v;
    runLookup(v);
  };
  certInput.addEventListener('blur', maybeAutoVerify);
  certInput.addEventListener('change', maybeAutoVerify);
}

/** Field names accepted by /properties basics validation — used for fieldFromApiError. */
export const BASICS_API_FIELDS = [
  'name', 'address_line1', 'postal_code', 'city', 'country', 'helyrajzi_szam', 'currency',
  'default_deposit_months', 'energy_rating',
  'energy_certificate_number', 'energy_certificate_expires_at',
];
